/**
 * Жизненный цикл Web Push-подписки (CLIENT_WEB.md §8.1–8.2, CC §9.7).
 *
 * Правила, которые здесь соблюдаются буквально:
 *  §8.1 п. 1 — `Notification.requestPermission()` вызывается ТОЛЬКО из
 *              обработчика жеста пользователя (экран N-03); автоматический
 *              запрос при загрузке ЗАПРЕЩЁН;
 *  §8.1 п. 3 — при `denied` повторный системный запрос ЗАПРЕЩЁН (N-04);
 *  §8.1 п. 4 — при `web_push_enabled = false` или `vapid_public_key = null`
 *              включение не предлагается вовсе;
 *  §8.2 п. 3 — при каждом запуске ключ активной подписки сверяется с
 *              конфигурацией, при расхождении — переподписка (решение D-38);
 *  §8.2 п. 4 — после ЛЮБОГО входа подписка прежней сессии недействительна,
 *              поэтому `POST /push/subscriptions` выполняется заново;
 *  §8.2 п. 6 — при выходе подписка удаляется на сервере и в браузере.
 */
import { api, authTransitionRequest, idempotency, unwrap, uuid } from '@shared/api/client'
import type { Config } from '@shared/api/types'
import { DeviceKey, deleteDeviceValue, deviceDb, getDeviceValue, setDeviceValue } from '@shared/db'
import { currentRegistration, registerServiceWorker } from '@shared/pwa/register'

import { base64UrlToBytes, subscriptionKeyMatches } from './vapid'

/**
 * WebKit иногда оставляет Promise регистрации/Push API незавершённым. Эти
 * операции при выходе best-effort и не должны удерживать локальный logout.
 */
const LOGOUT_PUSH_STEP_TIMEOUT_MS = 2_000
const PUSH_STEP_TIMED_OUT = Symbol('push step timed out')
const PUSH_CLEANUP_STORAGE_KEY = 'dvizh:push-cleanup-pending:v1'
let pushCleanupPendingInMemory = false

const PUSH_DEVICE_KEYS = [
  DeviceKey.pushSubscriptionId,
  DeviceKey.pushEndpoint,
  DeviceKey.pushApplicationServerKey,
  DeviceKey.pushResubscribeNeeded,
] as const

async function waitForPushStep<T>(
  task: () => Promise<T>,
  deadlineAt: number,
): Promise<T | typeof PUSH_STEP_TIMED_OUT> {
  const remainingMs = deadlineAt - Date.now()
  if (remainingMs <= 0) return PUSH_STEP_TIMED_OUT

  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<typeof PUSH_STEP_TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(PUSH_STEP_TIMED_OUT), remainingMs)
  })

  try {
    return await Promise.race([Promise.resolve().then(task), deadline])
  } finally {
    clearTimeout(timer)
  }
}

/* ===================================================================== */
/* Состояние разрешения                                                   */
/* ===================================================================== */

export type PushState =
  /** Движок не поддерживает Web Push — предложение не показывается (§8.1 п. 4). */
  | 'unsupported'
  /** Сервер отключил канал: `web_push_enabled = false` или ключа нет. */
  | 'server_disabled'
  /** Разрешение ещё не запрашивалось — доступен экран N-03. */
  | 'prompt'
  /** Разрешение выдано и подписка поддерживается. */
  | 'granted'
  /** Разрешение запрещено: N-04, повторный системный запрос невозможен. */
  | 'denied'

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof globalThis.PushManager !== 'undefined' &&
    typeof globalThis.Notification !== 'undefined'
  )
}

/** §8.1 п. 4: канал считается доступным только при включённом сервере и ключе. */
export function pushServerEnabled(config: Config | null): boolean {
  const push = config?.push
  return Boolean(push?.web_push_enabled && push.vapid_public_key)
}

export function pushState(config: Config | null): PushState {
  if (!pushSupported()) return 'unsupported'
  if (!pushServerEnabled(config)) return 'server_disabled'

  switch (Notification.permission) {
    case 'granted':
      return 'granted'
    case 'denied':
      return 'denied'
    default:
      return 'prompt'
  }
}

/* ===================================================================== */
/* Включение по действию пользователя (N-03)                              */
/* ===================================================================== */

export type EnableOutcome =
  | 'granted' // разрешение выдано, подписка зарегистрирована
  | 'granted_without_subscription' // разрешение есть, но service worker недоступен (dev)
  | 'denied' // пользователь отказал → N-04
  | 'dismissed' // системный диалог закрыт без ответа — состояние не изменилось
  | 'unsupported'
  | 'server_disabled'
  | 'failed' // разрешение есть, но подписаться не удалось

/**
 * ВЫЗЫВАТЬ ТОЛЬКО ИЗ ОБРАБОТЧИКА ЖЕСТА (§8.1 п. 1).
 *
 * Между жестом и `requestPermission()` не должно быть `await`, иначе часть
 * браузеров считает запрос не связанным с действием пользователя и блокирует
 * его; поэтому разрешение запрашивается первой же операцией.
 */
export async function enablePush(config: Config | null): Promise<EnableOutcome> {
  if (!pushSupported()) return 'unsupported'
  if (!pushServerEnabled(config)) return 'server_disabled'

  if (Notification.permission === 'denied') return 'denied'

  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission()

  if (permission === 'denied') return 'denied'
  if (permission !== 'granted') return 'dismissed'

  try {
    const result = await ensureSubscription(config, { force: true })
    return result === 'registered' ? 'granted' : 'granted_without_subscription'
  } catch (error) {
    console.warn('push subscription failed', error)
    return 'failed'
  }
}

/* ===================================================================== */
/* Синхронизация подписки при запуске и после входа                       */
/* ===================================================================== */

/**
 * Вызывается на каждом старте сессии (`startSession`), то есть и при запуске,
 * и сразу после входа. Разрешение при этом НЕ запрашивается: без выданного
 * разрешения функция молча выходит (§8.1 п. 1).
 *
 * Регистрация на сервере выполняется безусловно, даже если endpoint не
 * изменился: подписка привязана к сессии и после любого входа недействительна
 * (§8.2 п. 4, BS §8.5 п. 9).
 */
export async function syncPushSubscription(config: Config | null): Promise<void> {
  if (pushState(config) !== 'granted') return

  try {
    await ensureSubscription(config, { force: false })
  } catch (error) {
    console.warn('push subscription sync failed', error)
  }
}

type EnsureResult = 'registered' | 'no_worker'

/**
 * Приводит подписку браузера в соответствие с конфигурацией и регистрирует её
 * на сервере.
 *
 * Отсутствие service worker'а ошибкой не считается и отличается от неудачи:
 * в dev он намеренно не регистрируется (`devOptions.enabled = false`), а
 * разрешение при этом уже выдано и остальной интерфейс должен вести себя так,
 * будто уведомления включены.
 */
async function ensureSubscription(config: Config | null, options: { force: boolean }): Promise<EnsureResult> {
  const vapidKey = config?.push.vapid_public_key
  if (!vapidKey) return 'no_worker'

  // Предыдущий logout/switch мог оборваться на WebKit unsubscribe. Пока
  // старый endpoint не снят, его нельзя регистрировать под новым аккаунтом.
  if (await pushCleanupPending()) {
    await dropLocalPushSubscription()
    if (await pushCleanupPending()) return 'no_worker'
  }

  const registration = options.force ? await registerServiceWorker() : await currentRegistration()
  if (!registration) return 'no_worker'

  let subscription = await registration.pushManager.getSubscription()

  // §8.2 п. 3 (D-38): ключ подписки разошёлся с конфигурацией — переподписка.
  if (subscription && !subscriptionKeyMatches(subscription, vapidKey)) {
    await revokeOnServer()
    await dropLocalPushSubscription()
    if (await pushCleanupPending()) return 'no_worker'
    subscription = null
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      // §8.2 п. 6: обязателен, и из него следует правило «showNotification в каждой ветке».
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(vapidKey),
    })
  }

  const keys = readKeys(subscription)
  if (!keys) throw new Error('push subscription has no encryption keys')

  const created = unwrap(
    await api.POST('/push/subscriptions', {
      body: { platform: 'web', endpoint: subscription.endpoint, keys },
      ...idempotency(uuid()),
    }),
  )

  // §8.2 п. 2: id, endpoint и ключ нужны и странице, и service worker'у.
  await deviceDb.device.bulkPut([
    { key: DeviceKey.pushSubscriptionId, value: created.id },
    { key: DeviceKey.pushEndpoint, value: subscription.endpoint },
    { key: DeviceKey.pushApplicationServerKey, value: vapidKey },
  ])
  await deleteDeviceValue(DeviceKey.pushResubscribeNeeded)

  return 'registered'
}

/** Ключи шифрования подписки в base64url — как их отдаёт браузер. */
function readKeys(subscription: PushSubscription): { p256dh: string; auth: string } | null {
  const json = subscription.toJSON() as { keys?: { p256dh?: string; auth?: string } }
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!p256dh || !auth) return null
  return { p256dh, auth }
}

/* ===================================================================== */
/* Отключение (S-04) и выход (§8.2 п. 6)                                  */
/* ===================================================================== */

/**
 * Удаляет подписку на сервере и в браузере.
 *
 * Вызывается ДО `POST /auth/logout`: после выхода сессии уже нет и
 * `DELETE /push/subscriptions/{id}` ответит 401.
 */
export interface DropPushSubscriptionOptions {
  authTransition?: boolean
  suppressGlobalErrors?: boolean
}

export async function dropPushSubscription(options: DropPushSubscriptionOptions = {}): Promise<void> {
  const deadlineAt = Date.now() + LOGOUT_PUSH_STEP_TIMEOUT_MS
  await beginPushCleanup(deadlineAt)
  let storedSubscriptionId: string | null | typeof PUSH_STEP_TIMED_OUT = PUSH_STEP_TIMED_OUT

  try {
    storedSubscriptionId = await waitForPushStep(
      () => getDeviceValue<string>(DeviceKey.pushSubscriptionId),
      deadlineAt,
    )
    if (storedSubscriptionId !== PUSH_STEP_TIMED_OUT && storedSubscriptionId) {
      const revoked = await waitForPushStep(
        () => revokeSubscriptionOnServer(storedSubscriptionId as string, options),
        deadlineAt,
      )
      void revoked
    }
  } catch {
    /* Очистка IndexedDB/API при выходе выполняется best-effort. */
  }

  await dropLocalPushSubscription(deadlineAt)
}

/**
 * Смена владельца уже произошла на сервере, поэтому старую A-подписку
 * отозвать через cookie B нельзя. Снимаем browser endpoint до регистрации B,
 * чтобы один endpoint никогда не принадлежал двум аккаунтам.
 */
export async function dropLocalPushSubscription(
  deadlineAt = Date.now() + LOGOUT_PUSH_STEP_TIMEOUT_MS,
): Promise<void> {
  await beginPushCleanup(deadlineAt)
  const metadataCleanup = waitForPushStep(
    () => deviceDb.device.bulkDelete([...PUSH_DEVICE_KEYS]),
    deadlineAt,
  ).catch(() => PUSH_STEP_TIMED_OUT)

  let browserSubscriptionRemoved = false

  try {
    const registration = await waitForPushStep(currentRegistration, deadlineAt)
    if (registration === null) {
      browserSubscriptionRemoved = true
    } else if (registration !== PUSH_STEP_TIMED_OUT) {
      const subscription = await waitForPushStep(() => registration.pushManager.getSubscription(), deadlineAt)
      if (subscription === null) {
        browserSubscriptionRemoved = true
      } else if (subscription !== PUSH_STEP_TIMED_OUT) {
        // WebKit может навсегда оставить этот Promise pending. Logout и смена
        // владельца не должны из-за этого удерживать auth mutex и transport
        // fence: позднее завершение относится к уже полученному A-объекту.
        const unsubscribed = await waitForPushStep(() => subscription.unsubscribe(), deadlineAt)
        browserSubscriptionRemoved = unsubscribed === true
      }
    }
  } catch {
    /* браузер мог снять подписку сам */
  }

  await metadataCleanup

  if (browserSubscriptionRemoved) {
    await finishPushCleanup(deadlineAt)
  }
}

async function beginPushCleanup(deadlineAt: number): Promise<void> {
  pushCleanupPendingInMemory = true
  const fallbackPersisted = writePushCleanupFallback()
  const durableWrite = setDeviceValue(DeviceKey.pushCleanupPending, true)
  try {
    const persisted = await waitForPushStep(() => durableWrite, deadlineAt)
    if (persisted === PUSH_STEP_TIMED_OUT && !fallbackPersisted) {
      // Без единого durable barrier lifecycle остаётся fenced: после reload
      // старый endpoint иначе можно было бы привязать новому владельцу.
      await durableWrite
    }
  } catch {
    if (!fallbackPersisted) {
      // Fail closed: вызывающий auth transition не должен продолжить switch.
      throw new Error('push cleanup barrier is unavailable')
    }
  }
}

async function finishPushCleanup(deadlineAt: number): Promise<void> {
  try {
    const cleared = await waitForPushStep(() => deleteDeviceValue(DeviceKey.pushCleanupPending), deadlineAt)
    if (cleared !== PUSH_STEP_TIMED_OUT && clearPushCleanupFallback()) {
      pushCleanupPendingInMemory = false
    }
  } catch {
    /* Durable marker останется: следующий запуск повторит browser cleanup. */
  }
}

async function pushCleanupPending(): Promise<boolean> {
  if (pushCleanupPendingInMemory) return true
  if (readPushCleanupFallback()) return true
  return (await getDeviceValue<boolean>(DeviceKey.pushCleanupPending)) === true
}

function writePushCleanupFallback(): boolean {
  try {
    localStorage.setItem(PUSH_CLEANUP_STORAGE_KEY, '1')
    return localStorage.getItem(PUSH_CLEANUP_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function readPushCleanupFallback(): boolean {
  try {
    return localStorage.getItem(PUSH_CLEANUP_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function clearPushCleanupFallback(): boolean {
  try {
    localStorage.removeItem(PUSH_CLEANUP_STORAGE_KEY)
    return localStorage.getItem(PUSH_CLEANUP_STORAGE_KEY) === null
  } catch {
    return false
  }
}

async function revokeOnServer(): Promise<void> {
  const subscriptionId = await getDeviceValue<string>(DeviceKey.pushSubscriptionId)
  if (!subscriptionId) return

  await revokeSubscriptionOnServer(subscriptionId)
  await deleteSubscriptionIdIfCurrent(subscriptionId)
}

async function revokeSubscriptionOnServer(
  subscriptionId: string,
  options: DropPushSubscriptionOptions = {},
): Promise<void> {
  try {
    await api.DELETE('/push/subscriptions/{subscriptionId}', {
      params: { path: { subscriptionId } },
      ...(options.authTransition
        ? authTransitionRequest({ suppressGlobalErrors: options.suppressGlobalErrors })
        : {}),
    })
  } catch {
    // 404 — подписка уже удалена сервером вместе с сессией; это не ошибка.
  }
}

async function deleteSubscriptionIdIfCurrent(subscriptionId: string): Promise<void> {
  await deviceDb.transaction('rw', deviceDb.device, async () => {
    const current = await deviceDb.device.get(DeviceKey.pushSubscriptionId)
    if (current?.value === subscriptionId) {
      await deviceDb.device.delete(DeviceKey.pushSubscriptionId)
    }
  })
}

/**
 * §8.2 п. 5: service worker не смог переподписаться после
 * `pushsubscriptionchange` и оставил флаг — страница повторяет при запуске.
 */
export async function retryPushSubscriptionIfFlagged(config: Config | null): Promise<void> {
  const flagged = await getDeviceValue<boolean>(DeviceKey.pushResubscribeNeeded)
  if (!flagged) return

  await deleteDeviceValue(DeviceKey.pushResubscribeNeeded)
  await syncPushSubscription(config)
}
